import {
  ADD_CREATIVE,
  ADD_CREATIVES,
  ADD_DCM_CREATIVES,
  ADD_DIMENSIONS,
  EDIT_CREATIVE,
  DUPLICATE_CREATIVE,
  REMOVE_CREATIVE,
  SELECT_CREATIVE,
  DESELECT_CREATIVE,
  CLEAR_SELECTED_CREATIVES,
  SELECT_CREATIVES,
  DESELECT_CREATIVES,
  SET_WEIGHT_DISTRIBUTION,
  SET_ROTATION_TYPE,
  SET_ASSIGNED_PLACEMENTS,
  REORDER_CREATIVES,
  SET_START_DATE,
  SET_END_DATE,
  SET_HARD_CUTOFF,
  SET_CREATIVE_API_ERROR,
  ADD_BACKUP,
  ADD_COMPANION,
  RESET_CREATIVE_STATE,
  SET_DYNAMIC_TARGETING_KEYS,
  SET_WEIGHT_DISTRIBUTION_FLAG_ONLY
} from "./constants";

import { addCustomLandingPage } from "../dcm/landingPages";

import uuid from "uuid";

const defaultCreative = {
  taxonomy: {
    name: ""
  },
  creativeRole: "PRIMARY",
  file: {
    name: ""
  },
  dimensions: "N/A",
  weight: 0
};

export const addCreative = (creative) => ({
  type: ADD_CREATIVE,
  creative: { ...defaultCreative, ...creative, id: uuid.v4() }
});

export const addCreatives = (creatives) => ({
  type: ADD_CREATIVES,
  creatives: creatives.map((c) => ({
    ...defaultCreative,
    ...c,
    id: c.id ? c.id : uuid.v4()
  }))
});

export const addBackupCreatives = (primaryIds, backupCreative) => ({
  type: ADD_BACKUP,
  primaryIds,
  backupCreative: {
    ...defaultCreative,
    ...backupCreative,
    id: backupCreative.id ? backupCreative.id : uuid.v4(),
    creativeRole: "BACKUP"
  }
});

export const addCompanionCreatives = (primaryIds, companionCreative) => ({
  type: ADD_COMPANION,
  primaryIds,
  companionCreative: {
    ...defaultCreative,
    ...companionCreative,
    id: companionCreative.id ? companionCreative.id : uuid.v4(),
    creativeRole: "COMPANION",
    taxonomy: {
      ...defaultCreative.taxonomy,
      ...(companionCreative.taxonomy || {}),
      name: ""
    }
  }
});

export const addDcmCreatives = (dcmCreatives) => {
  let creatives = dcmCreatives.map((c) => {
    let cToReturn = {
      ...defaultCreative,
      taxonomy: {
        name: c.name
      },
      file: {
        ...defaultCreative.file,
        // Guarded: TRACKING_TEXT creatives have no size. The API documents
        // Creative.size as applicable to DISPLAY, DISPLAY_IMAGE_GALLERY,
        // FLASH_INPAGE, HTML5_BANNER, IMAGE and all RICH_MEDIA only, so
        // dereferencing it threw and took the picker down the moment a
        // site-served tracking creative was selected.
        size: c.size ? c.size.width + " x " + c.size.height : "N/A"
      },
      dcmId: c.id,
      dcmCreative: {
        ...c
      },
      id: uuid.v4()
    };

    // Guarded at every hop: GCM returns clickTags as an empty array for a
    // creative with none detected, and a click tag with no clickThroughUrl
    // at all, both of which used to throw and take the whole picker down.
    // A creative with no destination simply keeps no urlId, which leaves it
    // short of ready and the user owing a URL.
    const clickThroughUrl =
      ((cToReturn.dcmCreative.clickTags || [])[0] || {}).clickThroughUrl;

    if (clickThroughUrl) {
      cToReturn.taxonomy["urlId"] =
        clickThroughUrl.landingPageId || clickThroughUrl.customClickThroughUrl;
      //TODO: Find a way to trigger addCustomLandingPAge
    }

    return cToReturn;
  });

  return { type: ADD_DCM_CREATIVES, creatives };
};

export const addDimensions = (id, dimensions, primaryIds) => ({
  type: ADD_DIMENSIONS,
  id,
  dimensions,
  primaryIds
});

export const editCreative = (creative) => ({
  type: EDIT_CREATIVE,
  creative: { ...creative }
});

// Copies an already-uploaded creative. The copy is spliced in directly after
// its original (see the reducer) so a source card and its copies stay grouped.
// The new id is generated here to keep the reducer pure.
export const duplicateCreative = (id) => ({
  type: DUPLICATE_CREATIVE,
  id,
  newId: uuid.v4()
});

export const removeCreative = (id) => ({
  type: REMOVE_CREATIVE,
  id
});

export const selectCreative = (id) => ({
  type: SELECT_CREATIVE,
  id
});

export const deselectCreative = (id) => ({
  type: DESELECT_CREATIVE,
  id
});

export const clearSelectedCreatives = () => ({
  type: CLEAR_SELECTED_CREATIVES
});

// Bulk select/deselect in a single dispatch. The dimension groups on the
// naming step select a whole size at once, and looping selectCreative() over
// N ids would both re-render N times and leave selectedId pointing at the
// last card in the group rather than the first — the naming form reads
// selectedId, so the form would load the bottom card's taxonomy.
export const selectCreatives = (ids) => ({
  type: SELECT_CREATIVES,
  ids
});

export const deselectCreatives = (ids) => ({
  type: DESELECT_CREATIVES,
  ids
});

// creativeIds scopes the even/custom recompute to just that subset of
// creatives (e.g. one Smart Upload ad group) instead of every creative in
// the session. Omit it for the original account-wide behavior.
export const setWeightDistribution = (weightDistribution, creativeIds) => ({
  type: SET_WEIGHT_DISTRIBUTION,
  weightDistribution,
  creativeIds
});

// Sets the weightDistribution flag only, without recomputing any creative's
// weight. Used when restoring a previously-configured Smart Upload ad's
// settings so re-visiting its options step doesn't re-zero custom weights.
export const setWeightDistributionFlagOnly = (weightDistribution) => ({
  type: SET_WEIGHT_DISTRIBUTION_FLAG_ONLY,
  weightDistribution
});

export const setRotationType = (rotationType) => ({
  type: SET_ROTATION_TYPE,
  rotationType
});

export const setAssignedPlacements = (assignedPlacements) => ({
  type: SET_ASSIGNED_PLACEMENTS,
  assignedPlacements
});

// orderedVisibleIds is the new order of the creatives actually rendered as
// draggable (PRIMARY only, minus anything the list's filterFn hides). It's
// passed as ids rather than raw react-beautiful-dnd indices because those
// indices are positions within the *visible* list, which no longer line up
// with positions in allIds once backups/companions or a filterFn are in play.
export const reorderCreatives = (orderedVisibleIds) => ({
  type: REORDER_CREATIVES,
  orderedVisibleIds
});

export const setStartDate = (startDate) => ({
  type: SET_START_DATE,
  startDate
});

export const setEndDate = (endDate, timeSpecified = true) => ({
  type: SET_END_DATE,
  endDate,
  timeSpecified
});

export const setHardCutoff = (hardCutoff) => ({
  type: SET_HARD_CUTOFF,
  hardCutoff
});

export const setCreativeApiError = (error, isError = false) => ({
  type: SET_CREATIVE_API_ERROR,
  error,
  isError
});

export const resetCreativeState = () => ({
  type: RESET_CREATIVE_STATE
});

export const setDynamicTargetingKeys = (keys) => ({
  type: SET_DYNAMIC_TARGETING_KEYS,
  keys
});
